import type { TodoItem, TodosElsewhereResponse } from '@amumin/api-client';
import { t } from '@amumin/i18n';
import { Button } from '@amumin/ui';
import { cx, Icon, memberStyle } from '@amumin/ui-core';

import { api } from '../api.js';
import { formatDateKeyShort } from '../lib/dates.js';
import { activePersonal } from '../lib/navigation.js';
import { myOpenTodos, openTodosIn } from '../lib/todoSpace.js';
import { mutate, Skeleton, toastError, useLoad } from '../lib/ui.js';
import { appText, household, messages, ownMemberId } from '../state/session.js';
import { Panel, WidgetHead } from './HomeWidgets.js';

// Kaart To-do's op Vandaag (oprichter 6 okt 2026: "ik kan ook die widget niet toevoegen bij
// vandaag"). Jouw open to-do's in deze omgeving, belangrijk eerst (de volgorde van de API), met
// uiterlijk en afvinken. In Mijn omgeving daaronder ook wat een gezin aan jou toewees: alleen lezen,
// met een knop naar dat gezin (GET /v1/app/todos/elsewhere; niets gaat de andere kant op).
// Geen scroll in de kaart (ontwerpsysteem regel 13): hooguit zes regels per lijst, de rest valt van
// achteren af weg achter "+n meer" (home/cardFit.ts, MORE_HREF in TodayGrid).

const MAX_ROWS = 6;

/** Naar een gezin en daar Taken › To-do (andere omgeving: de pagina laadt opnieuw). */
async function openIn(householdId: string): Promise<void> {
  try {
    await openTodosIn(householdId);
  } catch (e) {
    toastError(e);
  }
}

export function TodosWidget({ tick }: { tick: number }) {
  const a = appText.value;
  const m = messages.value;
  const personal = activePersonal();
  const load = useLoad(() => api.app.todos.list(), [tick]);
  // Alleen in Mijn omgeving: de to-do's die een gezin aan jou toewees (een fout laat ze weg).
  const elsewhere = useLoad(
    (): Promise<TodosElsewhereResponse | null> =>
      personal ? api.app.todos.elsewhere().catch(() => null) : Promise.resolve(null),
    [tick, personal],
  );
  const items = load.data.value?.items ?? null;
  if (load.error.value && !items) return null;
  const open = myOpenTodos(items ?? [], { personal, me: ownMemberId.value });
  const members = household.value?.members ?? [];
  const others = elsewhere.data.value?.households ?? [];

  const complete = (item: TodoItem) =>
    void mutate(
      () =>
        api.app.taskInstances.complete({
          series_id: item.id,
          date: item.original_date,
          member_id: item.member_id,
          on_behalf_member_id: item.member_id,
        }),
      {
        success: t(a.tasks.completed, { title: item.title }),
        undo: () =>
          api.app.taskInstances.revert({
            series_id: item.id,
            date: item.original_date,
            member_id: item.member_id,
          }),
      },
    );

  const meta = (due: string | null, important: boolean, pending: boolean) =>
    important || due || pending ? (
      <span class="muted small block widget-ellipsis">
        {important ? <span class="tag tag-warn">{a.todo.important}</span> : null}{' '}
        {pending ? a.todo.widgetPending : null}
        {due && !pending ? t(a.todo.due, { date: formatDateKeyShort(due, m) }) : null}
      </span>
    ) : null;

  return (
    <Panel name="widget-todos" labelledBy="w-todos-title">
      <WidgetHead
        id="w-todos-title"
        title={a.home.widgets.todos.name}
        href="/to-do"
        link={a.todo.widgetAll}
      />
      {!items ? (
        <Skeleton rows={2} />
      ) : open.length === 0 ? (
        <p class="muted small" data-name="todos-empty">
          {a.todo.widgetEmpty}
        </p>
      ) : (
        <ul
          class="widget-list"
          data-name="todos-mine"
          data-fit-extra={Math.max(0, open.length - MAX_ROWS)}
        >
          {open.slice(0, MAX_ROWS).map((item) => {
            const member = members.find((x) => x.id === item.member_id);
            const pending = item.state === 'pending';
            return (
              <li
                key={`${item.key}-${item.member_id}`}
                class="widget-row"
                data-name="todo-card-row"
              >
                <button
                  type="button"
                  class={cx('check-button', pending && 'is-pending')}
                  style={member ? memberStyle(member.color_key) : undefined}
                  aria-label={`${t(a.tasks.completeFor, { name: member?.display_name ?? '' })}: ${item.title}`}
                  disabled={pending}
                  onClick={() => complete(item)}
                  data-name="todo-card-check"
                >
                  <Icon id={pending ? 'hourglass' : 'check'} size={20} />
                </button>
                <span class="widget-row-text">
                  <strong class="block widget-ellipsis">{item.title}</strong>
                  {meta(item.due_date, (item.sort_order ?? 0) < 0, pending)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {others.map((h) => (
        <div key={h.id} data-name="todos-elsewhere" data-household={h.id}>
          <div class="section-head">
            <h3 class="section-label">{t(a.todo.widgetFrom, { name: h.name })}</h3>
            <Button
              name="todos-open-in"
              size="small"
              variant="ghost"
              onPress={() => void openIn(h.id)}
            >
              {t(a.todo.widgetOpenIn, { name: h.name })}
            </Button>
          </div>
          <ul class="widget-list" data-fit-extra={Math.max(0, h.items.length - MAX_ROWS) + h.more}>
            {h.items.slice(0, MAX_ROWS).map((item) => (
              <li key={item.id} class="widget-row" data-name="todo-elsewhere-row">
                <span class="widget-row-text">
                  <span class="block widget-ellipsis">{item.title}</span>
                  {meta(item.due_date, item.important, item.state === 'pending')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </Panel>
  );
}
